import { safeFetchAPI } from '@/api';
import { utils } from '@/helpers';
import { Setter } from '@/legacy-types';
import type { CampaignFilters, CampaignsQueryResponse, CampaignsType, CampaignsV2Response, PutCampaignV2Args } from '@/types';
import { Pagination } from '@/types/campaigns';
import { clone } from 'lodash';
import { create } from 'zustand';

export type CampaignStoreState = {
	loaded: boolean;
	loading: boolean;

	pagination: Pagination | undefined;
	filters: CampaignFilters | undefined;
	campaignsMap: Record<string, CampaignsType>;

	findCampaign: (id: string) => CampaignsType | undefined;

	loadCampaigns: () => Promise<void>;
	setCampaign: (campaign: Partial<CampaignsType>) => void;
	getCampaign: (id: string) => Promise<CampaignsV2Response | undefined>;
	createCampaign: (template: CampaignsType) => Promise<CampaignsType | undefined>;
	updateCampaign: (template: CampaignsType, args?: PutCampaignV2Args) => Promise<CampaignsType | undefined>;
	setActiveStatus: (templateID: string, active: boolean) => Promise<boolean>;
	setArchivedStatus: (templateID: string, archived: boolean) => Promise<CampaignsType | undefined>;
	lockCampaign: (templateID: string, locked: boolean) => Promise<CampaignsType | undefined>;
	deleteCampaign: (templateID: string) => Promise<boolean>;

	setPage: (page: number) => void;
	setFilters: (filters: Setter<CampaignFilters>) => void;
};

export const useCampaignsStore = create<CampaignStoreState>((set, get) => ({
	loaded: false,
	loading: true,
	statsLoaded: false,
	campaignsMap: {},
	pagination: undefined,
	filters: undefined,
	findCampaign: (id: string) => get().campaignsMap?.[id],

	setCampaign: (campaign: Partial<CampaignsType>) => {
		if (!campaign.id) {
			return;
		}
		const campaignsMap = clone(get().campaignsMap ?? {});
		campaignsMap[campaign.id] = campaign as CampaignsType;
		set({ campaignsMap });
	},

	setPage: (page: number) => {
		set({ pagination: { ...get().pagination, page, limit: get().pagination?.limit ?? 10 } });
		get().loadCampaigns();
	},

	setFilters: (filters: Setter<CampaignFilters>) => {
		const currentFilters = get().filters ?? {};
		const nextFilters = typeof filters === 'function' ? filters(currentFilters) : filters;
		set({ filters: nextFilters });
		get().loadCampaigns();
	},

	async loadCampaigns() {
		set({ loading: true });

		const [response, error] =
			(await safeFetchAPI<CampaignsQueryResponse>('/campaigns/filtered/:uid', {
				method: 'POST',
				payload: {
					pagination: get().pagination,
					filters: get().filters,
				},
			})) || [];
		if (error) {
			utils?.toaster?.addToast?.({
				message: error || 'Error loading campaigns',
				type: 'danger',
			});
			return;
		}

		const campaignsMap: Record<string, CampaignsType> = {};
		for (const campaign of response?.campaigns ?? []) {
			campaignsMap[campaign.id] = campaign;
		}

		set({
			campaignsMap,
			pagination: response?.pagination,
			filters: response?.filters,
			loaded: true,
			loading: false,
		});
	},

	async getCampaign(id?: string) {
		set({ loading: true });

		const [response, error] = (await safeFetchAPI<CampaignsV2Response>(`/campaign/v2/:uid/${id}?ignoreCoMarketingError=true`)) || [];

		set({ loading: false });
		if (error) {
			utils?.toaster?.addToast?.({
				message: error || 'Error getting campaign',
				type: 'danger',
			});
			return undefined;
		}

		return response;
	},

	async createCampaign(campaign) {
		set({ loading: true });

		const [newCampaign, error] = (await safeFetchAPI<CampaignsType>('/campaign/:uid', { method: 'POST', payload: campaign })) || [];

		set({ loading: false });

		if (error) {
			utils?.toaster?.addToast?.({
				message: error || 'Error creating campaign',
				type: 'danger',
			});
			return undefined;
		}

		const campaignsMap = clone(get().campaignsMap ?? {});
		if (newCampaign) {
			campaignsMap[newCampaign.id] = newCampaign;
		}

		set({ campaignsMap });

		utils?.toaster?.addToast?.({
			message: 'Campaign created successfully',
			type: 'success',
		});

		return newCampaign;
	},

	async updateCampaign(campaign, args?: PutCampaignV2Args) {
		set({ loading: true });

		const [updatedCampaign, error] =
			(await safeFetchAPI<CampaignsType>(`/campaigns/v2/:uid/${campaign.id}`, {
				method: 'PUT',
				payload: {
					campaign,
				},
				queryParams: args,
			})) || [];

		set({ loading: false });

		if (error) {
			utils?.toaster?.addToast?.({
				message: error || 'Error updating campaign',
				type: 'danger',
			});
			return undefined;
		}

		const campaignsMap = clone(get().campaignsMap ?? {});
		if (updatedCampaign) {
			campaignsMap[updatedCampaign.id] = updatedCampaign;
		}

		set({ campaignsMap });

		return updatedCampaign;
	},

	async setArchivedStatus(id, archived) {
		set({ loading: true });

		const [updatedCampaign, error] = (await safeFetchAPI<CampaignsType>(`/campaigns/v2/:uid/${id}/${archived}`, { method: 'PUT' })) || [];
		set({ loading: false });

		if (error) {
			utils?.toaster?.addToast?.({
				message: error || 'Error setting archived status',
				type: 'danger',
			});
			return;
		}

		const campaignsMap = clone(get().campaignsMap ?? {});
		if (updatedCampaign) {
			campaignsMap[updatedCampaign.id] = updatedCampaign;
		}

		set({ campaignsMap });

		utils?.toaster?.addToast?.({
			message: `Campaign has been ${archived ? 'archived' : 'unarchived'}`,
			type: 'success',
		});

		return updatedCampaign;
	},

	async deleteCampaign(id) {
		set({ loading: true });

		const [_, error] = (await safeFetchAPI<undefined>(`/co-marketing/campaign/:uid/${id}`, { method: 'DELETE' })) || [];
		set({ loading: false });

		if (error) {
			utils?.toaster?.addToast?.({
				message: error || 'Error deleting campaign',
				type: 'danger',
			});
			return false;
		}

		const campaignsMap = clone(get().campaignsMap ?? {});
		delete campaignsMap[id];
		set({ campaignsMap });

		return true;
	},

	async setActiveStatus(id, active) {
		set({ loading: true });

		const [_, error] = (await safeFetchAPI<undefined>(`/campaign/status/v2/:uid/${id}/${active}`, { method: 'PUT' })) || [];

		set({ loading: false });

		if (error) {
			utils?.toaster?.addToast?.({
				message: error || 'Error setting active status',
				type: 'danger',
			});
			return false;
		}

		const campaignsMap = clone(get().campaignsMap ?? {});
		if (campaignsMap[id]) {
			campaignsMap[id].isActive = active;
		}

		set({ campaignsMap });

		utils?.toaster?.addToast?.({
			message: `Campaign has been ${active ? 'activated' : 'deactivated'}`,
			type: 'success',
		});

		return true;
	},

	async lockCampaign(id, locked) {
		set({ loading: true });

		const [updatedCampaign, error] = (await safeFetchAPI<CampaignsType>(`/campaign/lock/:uid/${id}/${locked}`, { method: 'PUT' })) || [];
		set({ loading: false });

		if (error) {
			utils?.toaster?.addToast?.({
				message: error || 'Error locking campaign',
				type: 'danger',
			});
			return;
		}

		const campaignsMap = clone(get().campaignsMap ?? {});
		if (updatedCampaign) {
			campaignsMap[updatedCampaign.id] = updatedCampaign;
		}

		set({ campaignsMap });

		return updatedCampaign;
	},
}));
